import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { PageBody } from "@/components/dashboard/DashboardShell";
import { Button } from "@/components/ui/button";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  getListingsOverviewMetrics,
  refreshListingsOverviewMetrics,
} from "@/lib/admin-metrics.functions";

export const Route = createFileRoute("/dashboard/admin/listings")({
  head: () => ({ meta: [{ title: "Listings · Admin" }, { name: "robots", content: "noindex" }] }),
  component: ListingsRoute,
});

function ListingsRoute() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (path === "/dashboard/admin/listings" || path === "/dashboard/admin/listings/") {
    return <ListingsOverview />;
  }
  return <Outlet />;
}

function ListingsOverview() {
  const fetchMetrics = useServerFn(getListingsOverviewMetrics);
  const refreshFn = useServerFn(refreshListingsOverviewMetrics);

  const [values, setValues] = useState<Record<string, number> | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchMetrics();
      setValues(res.values);
      setLastUpdated(res.lastUpdated);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load metrics");
    } finally {
      setLoading(false);
    }
  }, [fetchMetrics]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshFn();
      await load();
      toast.success("Metrics refreshed successfully.");
    } catch (err) {
      toast.error((err as Error).message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const fmt = (k: string) => {
    if (!values && loading) return "—";
    const v = values?.[k];
    return typeof v === "number" ? v.toLocaleString() : "—";
  };

  const cards: Array<{ label: string; key: string; sub: string; tone: string }> = [
    { label: "Total listings",   key: "total_listings",   sub: "All providers",        tone: "bg-slate-50 border-slate-200 text-slate-900" },
    { label: "Unclaimed",        key: "unclaimed",        sub: "Awaiting claim",       tone: "bg-amber-50 border-amber-200 text-amber-900" },
    { label: "Featured",         key: "featured",         sub: "Highlighted listings", tone: "bg-yellow-50 border-yellow-300 text-yellow-900" },
    { label: "Free claimed",     key: "free_claimed",     sub: "Claimed on free plan", tone: "bg-sky-50 border-sky-200 text-sky-900" },
    { label: "Paid claimed",     key: "paid_claimed",     sub: "Any paid plan",        tone: "bg-emerald-50 border-emerald-200 text-emerald-900" },
    { label: "Claimed monthly",  key: "claimed_monthly",  sub: "Premium monthly",      tone: "bg-indigo-50 border-indigo-200 text-indigo-900" },
    { label: "Claimed yearly",   key: "claimed_yearly",   sub: "Premium yearly",       tone: "bg-violet-50 border-violet-200 text-violet-900" },
    { label: "Claimed lifetime", key: "claimed_lifetime", sub: "Lifetime premium",     tone: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900" },
    { label: "Pending claims",   key: "pending_claims",   sub: "Awaiting review",      tone: "bg-orange-50 border-orange-200 text-orange-900" },
    { label: "Approved claims",  key: "approved_claims",  sub: "Approved",             tone: "bg-green-50 border-green-200 text-green-900" },
    { label: "Rejected claims",  key: "rejected_claims",  sub: "Rejected",             tone: "bg-rose-50 border-rose-200 text-rose-900" },
    { label: "Removed",          key: "removed",          sub: "Deleted profiles",     tone: "bg-red-50 border-red-300 text-red-900" },
  ];

  const lastUpdatedLabel = lastUpdated
    ? `Last updated ${formatRelative(new Date(lastUpdated))}`
    : values
      ? "No data available yet"
      : "Loading…";

  return (
    <PageBody className="space-y-4 pt-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[18px] font-semibold tracking-tight font-display">Listings overview</h1>
          <p className="text-[12px] text-muted-foreground mt-1">
            Provider directory health, claims pipeline, and plan distribution.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-muted-foreground">{lastUpdatedLabel}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh metrics
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map((s) => (
          <div key={s.key} className={`border p-4 rounded-md ${s.tone}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
              {s.label}
            </div>
            <div className="text-[22px] font-bold tabular-nums mt-1.5 leading-none">
              {fmt(s.key)}
            </div>
            <div className="text-[10px] mt-1.5 opacity-70">{s.sub}</div>
          </div>
        ))}
      </div>

      <p className="text-[12px] text-muted-foreground pt-2">
        Use the tabs above to manage listings, claims, requests, avatars, and data quality.
      </p>
    </PageBody>
  );
}

function formatRelative(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const sec = Math.max(1, Math.round(diffMs / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}
